import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import requestedDocumentList from "../../../api/requestedDocumentList";
import { AuthContext } from "../../../context/Auth/AuthContext";
import Loading from "../../material-ui/Loading";
import RequestedDocumentsTable from "./RequestedDocumentsTable";
import RequestDocumentsPopup from "./RequestDocumentsPopup";
import DocumentChasingPage from "./DocumentChasingPage";
import getPendingChasingDocuments from "../../../api/getPendingChasingDocuments";
import { Tooltip } from "@mui/material";
import { Mail } from "lucide-react";
import AddBeneficiaryPopup from "../client-documents/all-documents/AddBeneficiaryPopup";
import UploadDocumentsPopup from "./all-documents/UploadDocumentsPopup";
import baseUrl from "../../../config/baseUrl";
import getAllFieldTypeList from "../../../api/getAllFieldTypeList";
import DocumentTable from "./all-documents/uploaded/DocumentTable";
import { Button } from "../../../shadcn-components/ui/button";
import BeneficiaryTable from "./all-documents/BeneficiaryTable";
import { FilePenLineIcon, FolderUp } from "lucide-react";
import clientsData from "../../../api/clients";
import { useDebounce } from "use-debounce";
import BeneficiaryTabList from "./all-documents/beneficiary/BeneficiaryTabList";
import PdfViewer from "../../pdf-viewer/PdfViewer";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useParams } from "react-router-dom";
import { useRoleContext } from "../../../context/RolesContext";

const RequestedDocumentsList = ({
  clientID,
  clientEmail,
  clientType,
  clientDocumentsType,
  selectedClientDocumentsType,
  changeSelectedClientDocumentsType,
  setShowClientIdSearch,
  showUploads,
  beneficiary,
  typeOptions,
  clientSubType,
  clientPhone,
  onOpenDocumentChange,
  // Clients > Document Chasing: show only the Document Chasing view for the
  // selected client (no Requested / Uploaded tabs or their actions).
  chasingOnly = false,
  // Full client row from the Clients list (UI display only - Client ID and
  // the Edit Client action on the Document Chasing page).
  clientRecord,
}) => {
  const [loading, setLoading] = useState(false);
  const [bulkDeleteRequests, setBulkDeleteRequests] = useState(false);
  const [documentResponse, setDocumentResponse] = useState([]);

  // ─── Lazy-loaded lists ─────────────────────────────────────────────────────
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const listsLoadedRef = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentUploadPage, setCurrentUploadPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [categoryUpdated, setCategoryUpdated] = useState(0);
  const [documentUpdatedToTrue, setDocumentUpdatedToTrue] = useState(0);
  const [beneficiaryAdded, setBeneficiaryAdded] = useState(false);
  const [selectedDocCategoryType, setSelectedDocCategoryType] = useState("All");
  const [contactBeneficiary, setContactBeneficiary] = useState(beneficiary);
  const [emailsToBeSent, setEmailsToBeSent] = useState("");

  // PDF viewer state
  const [documentViewer, setDocumentViewer] = useState(false);
  const [pdfLink, setPdfLink] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [pdfViewerTitle, setPdfViewerTitle] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [name, setName] = useState("");
  const [docCaseID, setDocCaseID] = useState("");
  const [docReqID, setDocReqID] = useState("");
  const [reqStatus, setReqStatus] = useState("");
  const [description, setDescription] = useState("");
  const [ocrData, setOcrData] = useState([]);
  const [selectedRequestLabel, setSelectedRequestLabel] = useState("");
  const [docLinkedReqName, setDocLinkedReqName] = useState("");
  const [docCreatedAt, setDocCreatedAt] = useState("");

  const { token, user_id, is_admin } = useContext(AuthContext);
  const { permissions } = useRoleContext();
  const { cid, bid } = useParams();

  let reqBenId = null;
  if (bid !== "null" && bid !== null && cid === clientID) reqBenId = bid;
  const [selectedTab, setSelectedTab] = useState(reqBenId);

  const [selectedClientId, setSelectedClientId] = useState({
    primary: true,
    id: clientID,
    email: clientEmail,
    phone_number: clientPhone,
  });

  const [selectedDocumentRequestedType, setSelectedDocumentRequestedType] =
    useState(chasingOnly ? "documentChasing" : beneficiary ? "allBeneficiary" : "uploaded");
  const [pendingChasingCount, setPendingChasingCount] = useState(0);
  const [selectedDocumentCategoryArray, setSelectedDocumentCategoryArray] =
    useState(selectedClientDocumentsType?.toLowerCase());

  const prevStateRef = useRef({ debouncedGlobalFilter, columnFilters });

  const hasPermission = useCallback(
    (p) => {
      if (is_admin === "1") return true;
      return permissions?.includes(p);
    },
    [is_admin, permissions],
  );

  const canViewBeneficiaries = hasPermission("view_beneficiaries");
  const canViewRequestedDocuments = hasPermission("view_requested_documents");
  const canRequestDocuments = hasPermission("request_documents");
  const canUploadDocuments = hasPermission("upload_documents");

  function isValidEmail(email) {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  }

  useEffect(() => {
    if (isValidEmail(clientEmail)) setEmailsToBeSent(clientEmail);
  }, [clientEmail]);

  // Keeps the "Document Chasing" tab button's enabled/disabled state accurate.
  useEffect(() => {
    if (!selectedClientId.id) return;
    const fetchPendingChasingCount = async () => {
      const res = await getPendingChasingDocuments({
        token,
        contact_id: selectedClientId.id,
      });
      setPendingChasingCount(res?.code === 200 ? res.data?.length || 0 : 0);
    };
    fetchPendingChasingCount();
  }, [selectedClientId.id, token, categoryUpdated]);

  const handleDocumentChasingClick = () => {
    handleRequestStatusButtonClick("documentChasing");
  };

  // ─── Lazy-load category/subCategory lists only when the "Uploaded" tab is first opened ──
  const loadListsIfNeeded = useCallback(async () => {
    if (listsLoadedRef.current) return;
    listsLoadedRef.current = true;
    const [catRes, subCatRes] = await Promise.all([
      getAllFieldTypeList({ token, type: "document_type" }),
      getAllFieldTypeList({ token, type: "document_subtype" }),
    ]);
    setCategoryList(catRes.code === 200 ? catRes.data : []);
    setSubCategoryList(subCatRes.code === 200 ? subCatRes.data : []);
  }, [token]);
  useEffect(() => {
    if (selectedDocumentRequestedType === "uploaded") {
      loadListsIfNeeded();
    }
  }, []); // runs once on mount only

  const handleRequestStatusButtonClick = useCallback(
    async (type) => {
      if (chasingOnly && type !== "documentChasing") return;
      setSelectedDocumentRequestedType(type);
      setContactBeneficiary(false);
      // Only load lists when switching to the Uploaded tab
      if (type === "uploaded") {
        await loadListsIfNeeded();
      }
    },
    [loadListsIfNeeded, chasingOnly],
  );

  const getRequestNameDetails = async (reqId) => {
    const url = `${baseUrl}/api/get-request-name?document_request_id=${reqId}`;
    const res = await clientsData({ token, api: url });
    setSelectedRequestLabel(res.data[0].document_name);
  };

  // Loads the document's own header details (added date and the full
  // "Service - Request" linked name) via get-all-doc-data - the source the
  // Uploaded tab and All Documents use - so Requested, Uploaded and
  // beneficiary tabs all show the same header. contact_id is the tab's
  // contact (primary or beneficiary) so non-admins' own documents are found.
  // On failure the values taken from the clicked row stay.
  const headerDocRef = useRef(null);
  const loadViewerHeaderInfo = async (documentId, row) => {
    headerDocRef.current = documentId;
    try {
      const params = new URLSearchParams({
        document_id: documentId,
        contact_id: selectedClientId.id,
        isAdmin: is_admin,
        userId: user_id,
      });
      const res = await clientsData({
        token,
        api: `${baseUrl}/api/get-all-doc-data?${params}`,
      });
      const doc = res?.data?.[0]?.document?.find(
        (d) => d.document_id === documentId,
      );
      // Ignore a late answer for a document that is no longer open.
      if (!doc || headerDocRef.current !== documentId) return;
      setDocLinkedReqName(doc.document_request_setting_name || "");
      setDocCreatedAt(doc.created_at || "");
    } catch (error) {
      // keep the row-based values
    }
  };

  const changeDocumentViewerState = (
    id,
    requestId,
    requestStatus,
    link,
    data,
    name,
    category,
    description,
    document_sub_category,
    document_type,
    row,
  ) => {
    setSelectedRequestLabel("");
    // Viewer header info (linked request name + added date). Uploaded rows
    // already carry both; Requested rows come from a different API with only
    // the request's own date and the bare request name, so for both tabs the
    // document's real values are loaded from the same API All Documents uses.
    setDocLinkedReqName(row?.document_request_setting_name || "");
    setDocCreatedAt(
      selectedDocumentRequestedType === "uploaded" ? row?.created_at || "" : "",
    );
    loadViewerHeaderInfo(id, row);
    setDocumentType(document_type);
    setPdfLink(link);
    setName(name);
    setDocCaseID(id);
    setDocReqID(requestId);
    if (requestId) getRequestNameDetails(requestId);
    setReqStatus(requestStatus);
    setDescription(description);
    setCategory(category);
    setSubCategory(document_sub_category);
    setPdfViewerTitle(name);
    setDocumentViewer(true);
    onOpenDocumentChange?.(name);
  };

  const closeDocumentViewer = () => {
    setDocumentViewer(false);
    onOpenDocumentChange?.(null);
  };

  const documentResponseFunc = useCallback(async () => {
    if (!categoryUpdated) setLoading(true);

    const offset = currentPage - 1;
    const filterParams = {};
    columnFilters.forEach((f) => {
      filterParams[f.id] = f.value;
    });

    const sortParam = sorting
      .map((s) => `${s.id}=${s.desc ? "desc" : "asc"}`)
      .join(",");
    const docStatus = `&document_status=${selectedDocumentRequestedType}`;
    const docCategory = `&document_category=${selectedDocumentCategoryArray}`;

    const params = {
      isAdmin: is_admin,
      userId: user_id,
      contact_id: selectedClientId.id,
      limit: pageSize,
      offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,
    };

    const url = `${baseUrl}/api/get-doc-request-data?${new URLSearchParams(params)}${docStatus}${docCategory}`;
    const response = await requestedDocumentList({ token, api: url });

    if (response.statusCode === 200) {
      const allDocuments =
        response.data?.alldocumentcasescount !== 0
          ? response.data?.alldocumentcases || []
          : [];
      setTotalPages(
        Math.ceil(response.data?.alldocumentcasescount / pageSize),
      );
      setDocumentResponse(allDocuments);
    } else {
      setTotalPages(1);
      setDocumentResponse([]);
    }
    if (!categoryUpdated) setLoading(false);
  }, [
    categoryUpdated,
    currentPage,
    columnFilters,
    sorting,
    selectedDocumentRequestedType,
    selectedDocumentCategoryArray,
    pageSize,
    debouncedGlobalFilter,
    is_admin,
    user_id,
    selectedClientId,
    token,
  ]);

  useEffect(() => {
    if (cid || bid) handleRequestStatusButtonClick("allRequested");
  }, []);

  useEffect(() => {
    if (showUploads) handleRequestStatusButtonClick("uploaded");
  }, [showUploads]);

  useEffect(() => {
    if (
      !canViewRequestedDocuments &&
      selectedDocumentRequestedType === "allRequested"
    ) {
      handleRequestStatusButtonClick("uploaded");
    }
  }, [canViewRequestedDocuments]);

  useEffect(() => {
    // Don't fetch — the "uploaded" tab has its own DocumentTable
    // that handles its own data fetching internally
    if (selectedDocumentRequestedType === "uploaded") return;
    // Also skip beneficiary tab — it uses BeneficiaryTable which is self-contained
    if (selectedDocumentRequestedType === "allBeneficiary") return;
    // Document Chasing has its own data fetching (chasing summary/pending/templates)
    if (selectedDocumentRequestedType === "documentChasing") return;

    const currentState = { debouncedGlobalFilter, columnFilters };
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    prevStateRef.current = currentState;
    documentResponseFunc();
  }, [
    categoryUpdated,
    documentUpdatedToTrue,
    bulkDeleteRequests,
    beneficiaryAdded,
    selectedDocumentRequestedType,
    selectedDocumentCategoryArray,
    currentPage,
    columnFilters,
    debouncedGlobalFilter,
    sorting,
    pageSize,
    selectedClientId,
  ]);
  const handleTabChange = (beneficiaryId, index, email, phoneNumber) => {
    setSelectedTab(beneficiaryId);
    setSelectedClientId({
      primary: index === 0,
      id: beneficiaryId,
      email,
      phone_number: phoneNumber || "",
    });
  };

  return (
    <>
      {!documentViewer ? (
        <div className="ben">
          {canViewBeneficiaries && (
            <BeneficiaryTabList
              clientID={clientID}
              onTabChange={handleTabChange}
              selectedTab={selectedTab}
              emailsToBeSent={emailsToBeSent}
              setEmailsToBeSent={setEmailsToBeSent}
              isValidEmail={isValidEmail}
            />
          )}

          {!chasingOnly && (
          <div className="case-doc-table-header flex-wrap gap-4 py-3 lg:flex-nowrap lg:!py-0">
            <div className="doc-buttons min-w-[200px]">
              {(contactBeneficiary ||
              selectedDocumentRequestedType.toLowerCase() ===
                "allbeneficiary") && (
                <AddBeneficiaryPopup
                  clientID={clientID}
                  setBeneficiaryAdded={setBeneficiaryAdded}
                  clientType={clientType}
                  typeOptions={typeOptions}
                />
              )}
            </div>

            <div className="flex border border-black rounded-md">
              {/* Document Chasing tab - hidden from Clients page UI; uncomment to re-enable */}
              {/* <Button
                className={`p-3 capitalize rounded-none rounded-tl-md rounded-bl-md text-[#292929] shadow-none flex gap-2 font-semibold ${
                  selectedDocumentRequestedType === "documentChasing"
                    ? "bg-[#2b93c8d0] hover:bg-[#2b94c8]"
                    : "bg-transparent"
                }`}
                disabled={pendingChasingCount === 0}
                onClick={handleDocumentChasingClick}
              >
                <Mail size={16} />
                Document Chasing
              </Button> */}

              {canViewRequestedDocuments && (
                <Button
                  className={`p-3 capitalize rounded-none rounded-tl-md rounded-bl-md text-[#292929] shadow-none flex gap-2 font-semibold ${
                    selectedDocumentRequestedType === "allRequested"
                      ? "bg-[#2b93c8d0] hover:bg-[#2b94c8]"
                      : "bg-transparent"
                  }`}
                  onClick={() => handleRequestStatusButtonClick("allRequested")}
                >
                  <FilePenLineIcon color="#292929" />
                  Requested
                </Button>
              )}

              <Button
                className={`p-3 capitalize rounded-none rounded-tr-md rounded-br-md text-[#292929] shadow-none flex gap-2 font-semibold ${
                  selectedDocumentRequestedType === "uploaded"
                    ? "bg-[#2b93c8d0] hover:bg-[#2b94c8]"
                    : "bg-transparent"
                }`}
                onClick={() => handleRequestStatusButtonClick("uploaded")}
              >
                <FolderUp color="#292929" />
                Uploaded
              </Button>
            </div>

            <div
              className={`search-area ${selectedDocumentRequestedType === "allBeneficiary" ? "invisible" : "visible"}`}
            >
              <FormControl className="case-doc-select-ui">
                {selectedDocumentRequestedType?.toLowerCase() ===
                "allrequested" ? (
                  <>
                    <InputLabel
                      className="case-doc-select-label"
                      id="select-label"
                    >
                      Document Request Type
                    </InputLabel>
                    <Select
                      sx={{ margin: "10px 0px", maxWidth: "20" }}
                      labelId="select-label"
                      variant="outlined"
                      id="simple-select"
                      value={selectedClientDocumentsType}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setSelectedDocumentCategoryArray(
                          e.target.value.toLowerCase(),
                        );
                        changeSelectedClientDocumentsType(e);
                      }}
                      label="Document Request Type"
                    >
                      {clientDocumentsType.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                ) : (
                  <>
                    <InputLabel
                      className="case-doc-select-label"
                      id="select-label"
                    >
                      Category
                    </InputLabel>
                    <Select
                      sx={{ margin: "10px 0px", maxWidth: "20" }}
                      labelId="select-label"
                      variant="outlined"
                      id="simple-select"
                      value={selectedDocCategoryType}
                      onChange={(e) => {
                        setCurrentUploadPage(1);
                        setSelectedDocCategoryType(e.target.value);
                      }}
                      label="Category"
                    >
                      <MenuItem key="All" value="All">
                        All
                      </MenuItem>
                      {categoryList?.map((option) => (
                        <MenuItem
                          key={option.field_setting_id}
                          value={option.field_setting_id}
                        >
                          {option.fields_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              </FormControl>
            </div>
          </div>
          )}

          {selectedDocumentRequestedType === "allRequested" ? (
            <div className="document__table">
              <RequestedDocumentsTable
                clientID={selectedClientId.id}
                setCategoryUpdatedToTrue={() =>
                  setCategoryUpdated((c) => c + 1)
                }
                documentResponse={documentResponse}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                pageSize={pageSize}
                setPageSize={setPageSize}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                bulkDeleteRequests={bulkDeleteRequests}
                setBulkDeleteRequests={setBulkDeleteRequests}
                sorting={sorting}
                setSorting={setSorting}
                changeDocumentViewerState={changeDocumentViewerState}
                actionButton={
                  canRequestDocuments ? (
                    <RequestDocumentsPopup
                      key={selectedClientId.id}
                      clientID={selectedClientId}
                      setCategoryUpdatedToTrue={() =>
                        setCategoryUpdated((c) => c + 1)
                      }
                      clientType={clientType}
                      setSelectedClientId={setSelectedClientId}
                      clientPrimaryID={clientID}
                      clientSubType={clientSubType}
                      emailsToBeSent={emailsToBeSent}
                    />
                  ) : null
                }
              />
            </div>
          ) : selectedDocumentRequestedType === "allBeneficiary" ? (
            <BeneficiaryTable
              clientID={clientID}
              beneficiaryAdded={beneficiaryAdded}
              setBeneficiaryAdded={setBeneficiaryAdded}
              typeOptions={typeOptions}
            />
          ) : selectedDocumentRequestedType === "documentChasing" ? (
            <DocumentChasingPage
              clientID={selectedClientId.id}
              clientPhone={selectedClientId.phone_number}
              clientRecord={selectedClientId.primary ? clientRecord : null}
              typeOptions={typeOptions}
            />
          ) : (
            <DocumentTable
              clientID={selectedClientId.id}
              selectedDocCategoryType={selectedDocCategoryType}
              changeDocumentViewerState={changeDocumentViewerState}
              documentUpdatedToTrue={documentUpdatedToTrue}
              setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
              subCategoryList={subCategoryList}
              currentUploadPage={currentUploadPage}
              setCurrentUploadPage={setCurrentUploadPage}
              actionButton={
                canUploadDocuments ? (
                  <UploadDocumentsPopup
                    clientID={selectedClientId.id}
                    documentUpdatedToTrue={documentUpdatedToTrue}
                    setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
                    subCategoryList={subCategoryList}
                  />
                ) : null
              }
            />
          )}
        </div>
      ) : (
        <DndProvider backend={HTML5Backend}>
          <PdfViewer
            documentType={documentType}
            clientID={clientID}
            // Always passed: the viewer only falls back to clientID when benID
            // is null or "" - an omitted (undefined) benID left the "Link to
            // Request List" dropdown empty. Primary tab = "" (use the client);
            // a beneficiary tab = that contact, like All Documents' ben_id.
            benID={selectedClientId.primary ? "" : selectedClientId.id}
            documentID={docCaseID}
            documentTitle={pdfViewerTitle}
            documentURL={pdfLink}
            documentCreatedAt={docCreatedAt}
            linkedDocReqName={docLinkedReqName}
            document_table="documents"
            documentRequestID={docReqID}
            existingDocumentRequestStatus={reqStatus}
            documentCategory={category}
            documentDesc={description}
            documentSubCategory={subCategory}
            clientPhone={clientPhone}
            currentTabId={docCaseID}
            closeTabById={closeDocumentViewer}
            changeActiveTab={closeDocumentViewer}
            fromTable="RequestedDocumentsList"
            refetchDocumentTable={documentResponseFunc}
          />
        </DndProvider>
      )}
    </>
  );
};

export default RequestedDocumentsList;
